import React, { useState, useEffect, useRef } from 'react';
import type { AxiosError } from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Checkbox,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  InsertDriveFile as FileIcon,
  MenuBook as BookIcon,
  ViewModule as ViewIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AttachFile as ResourceIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  PlayArrow as PlayArrowIcon,
  VideoLibrary,
  Image,
  PictureAsPdf,
  Link as LinkIcon,
  Upload,
  School,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import QuizConfigurationModal from '../components/Admin/QuizConfigurationModal';
import { getQuizConfig, QuizConfigDTO } from '../services/quizService';
import {
  getCourseById,
  CourseDTO,
  getCoreTopicsByCurriculumSubject,
  CoreTopicDTO,
  getEOHierarchyByCurriculum, // Updated import
  EOHierarchyDTO,
  addTopicToCourse,
  AddTopicToCourseRequest,
  getAllBooks,
  createBook,
  updateBook,
  deleteBook,
  BookDTO,
  getViewsByCourse,
  getBookViewsByCourse,
  createView,
  updateView,
  deleteView,
  setActiveVersion,
  CourseViewDTO,
  // Graph APIs
  getCourseStructure,
  addCourseStructureNode,
  createCourseCustomTopic,
  getCourseCustomTopics,
  updateCourseStructureNode,
  deleteCourseStructureNode,
  moveCourseStructureNode,
  publishCourseStructure,
  deleteCourseStructure,
  saveCourseStructure,
  type CourseStructureNodeDTO,
  // Versioning
  getCourseStructureVersions,
  restoreCourseStructureVersion,
  deleteCourseStructureVersion,
  type CourseViewVersionDTO
} from '../services/adminCourseService';

import adminApi from '../services/adminApi';
import {
  buildCurriculumStructureFromEoHierarchy,
  collectCoreTopicIds,
  filterTopicRefsByAllowedIds,
  mergeCurriculumScaffoldWithPersistedNodes,
} from '../utils/courseStructureUtils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function applyNodeIdReplacements(
  nodes: CourseStructureNodeDTO[],
  idReplacements: Map<number, number>
): CourseStructureNodeDTO[] {
  return nodes.map((node) => ({
    ...node,
    id: idReplacements.get(node.id) ?? node.id,
    children: node.children
      ? applyNodeIdReplacements(node.children, idReplacements)
      : node.children,
  }));
}

/** User-visible explanation when course / EO / Scottish API load fails (incl. CORS / network). */
function describeCourseDataLoadFailure(err: unknown): string {
  const ax = err as AxiosError | undefined;
  const code = ax?.code;
  const msg = (ax?.message || '').toLowerCase();
  const noResponse = ax?.response === undefined;
  if (
    code === 'ERR_NETWORK'
    || ax?.message === 'Network Error'
    || (noResponse && msg.includes('network'))
  ) {
    const origin =
      typeof window !== 'undefined' ? window.location.origin : 'this site';
    return (
      `Experience outcome hierarchy and Scottish curriculum lookups could not be loaded — the browser blocked or could not reach the Scottish curriculum API (network / CORS). ` +
      `Ensure \"${origin}\" is allowed by CORS on scottish-curriculum-service. Until fixed, EO-based structure and some curriculum tools may stay empty or outdated.`
    );
  }
  const data = ax?.response?.data as unknown;
  const serverMsg =
    data && typeof data === 'object' && data !== null && 'message' in data
      ? String((data as { message?: string }).message)
      : '';
  const status = ax?.response?.status;
  const detail =
    serverMsg || ax?.response?.statusText || (status ? `HTTP ${status}` : '') || (err instanceof Error ? err.message : 'Unknown error');
  return (
    `Could not finish loading course curriculum data (${detail}). ` +
    `Topics from the topic library may still appear; experience-outcome hierarchy and Scottish-linked actions may fail until this is resolved.`
  );
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`content-tabpanel-${index}`}
      aria-labelledby={`content-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ContentManagement: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [structureNodes, setStructureNodes] = useState<CourseStructureNodeDTO[]>([]);
  const [structureLoading, setStructureLoading] = useState(false);
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<number>>(new Set());
  const [course, setCourse] = useState<CourseDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [quizConfig, setQuizConfig] = useState<QuizConfigDTO | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Versioning State
  const [versions, setVersions] = useState<CourseViewVersionDTO[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null); // null = Draft
  const [dragOverNodeId, setDragOverNodeId] = useState<number | null>(null);
  /** Deepest structure node under the cursor (level breaks ties when dragenter bubbles). */
  const dragOverTargetRef = useRef<{ id: number; level: number } | null>(null);
  /** Ignore stale loadStructure responses after course/view changes. */
  const structureLoadGeneration = useRef(0);

  const clearStructureDragOver = () => {
    dragOverTargetRef.current = null;
    setDragOverNodeId(null);
  };

  /** Operation status for course structure actions (delete, add, move, publish) so user sees backend call result */
  const [operationStatus, setOperationStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [operationMessage, setOperationMessage] = useState<string>('');

  // Core topic library (now loaded from backend)
  const [coreTopics, setCoreTopics] = useState<CoreTopicDTO[]>([]);
  const [courseTopics, setCourseTopics] = useState<any[]>([]); // Topics in this course
  const [eoHierarchy, setEOHierarchy] = useState<EOHierarchyDTO[]>([]); // EO hierarchy for the curriculum subject
  const [views, setViews] = useState<any[]>([]);
  const [books, setBooks] = useState<any[]>([]);
  const [resources, setResources] = useState<any[]>([]);

  // Dialog states
  const [topicDialogOpen, setTopicDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [contentEditorOpen, setContentEditorOpen] = useState(false);

  // Selected items
  const [selectedTopic, setSelectedTopic] = useState<any>(null);
  const [selectedView, setSelectedView] = useState<any>(null);
  const [selectedBook, setSelectedBook] = useState<any>(null);
  const [selectedResource, setSelectedResource] = useState<any>(null);

  // View dialog state
  const [viewType, setViewType] = useState<'DEFAULT' | 'BOOK_BASED'>('DEFAULT');
  const [selectedBookId, setSelectedBookId] = useState<number | null>(null);
  const [viewFormData, setViewFormData] = useState<{ name: string; isDefault: boolean }>({
    name: '',
    isDefault: false,
  });

  // Book dialog form state
  const [bookFormData, setBookFormData] = useState<BookDTO>({
    title: '',
    publisher: '',
    authors: '',
    isbn: '',
    edition: '',
    year: undefined,
    coverImageUrl: '',
    description: '',
  });

  // Menu anchor
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuItem, setMenuItem] = useState<any>(null);

  // Add-child dialog for structure nodes
  const [addChildDialogOpen, setAddChildDialogOpen] = useState(false);
  const [addChildParent, setAddChildParent] = useState<CourseStructureNodeDTO | null>(null);
  const [addChildMode, setAddChildMode] = useState<'wrapper' | 'topic'>('wrapper');
  const [addChildName, setAddChildName] = useState('');
  const [courseLocalTopicIds, setCourseLocalTopicIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!courseId) return;

    // Reset course-scoped state so a prior course's structure/views cannot leak into this one.
    setSelectedView(null);
    setSelectedVersionId(null);
    setStructureNodes([]);
    setVersions([]);
    setViews([]);
    setCoreTopics([]);
    setCourseTopics([]);
    setEOHierarchy([]);
    setCourse(null);
    setCourseLocalTopicIds(new Set());
    setLoading(true);
    setError(null);
    setOperationStatus('idle');
    setOperationMessage('');
    structureLoadGeneration.current += 1;

    const init = async () => {
      await loadCourse();
      loadVersions();
      await loadStructure();
      loadViews();
      loadBooks();
      loadMockResources();
    };
    init();
  }, [courseId]);

  // Reload structure when selected version changes
  useEffect(() => {
    if (courseId) {
      loadStructure();
    }
  }, [selectedVersionId]);

  // When a view is selected (e.g. auto-selected after load), reload versions and structure so APIs get viewId
  useEffect(() => {
    if (courseId && selectedView?.id) {
      structureLoadGeneration.current += 1;
      loadVersions();
      loadStructure();
    }
  }, [selectedView?.id]);

  // Re-merge structure when EO hierarchy arrives or changes (avoids stale scaffold from prior course/subject).
  useEffect(() => {
    if (!courseId || !course?.curriculumId || !course?.curriculumSubjectId) return;
    structureLoadGeneration.current += 1;
    loadStructure();
  }, [eoHierarchy, course?.curriculumId, course?.curriculumSubjectId]);

  // Re-apply topic filter when core topic library or course-local topics load after structure
  useEffect(() => {
    if (!courseId || structureNodes.length === 0) return;
    const allowedTopicIds = collectCoreTopicIds(coreTopics);
    const filtered = filterTopicRefsByAllowedIds(structureNodes, allowedTopicIds, courseLocalTopicIds);
    if (JSON.stringify(filtered) !== JSON.stringify(structureNodes)) {
      setStructureNodes(filtered);
    }
  }, [coreTopics, courseLocalTopicIds, courseId]);

  // Load core topics and EO hierarchy once the course (and its curriculumSubjectId) is known
  useEffect(() => {
    const loadDataForCourse = async () => {
      if (!course) {
        return;
      }
      try {
        // Load core topics (Still anchored to Subject currently?)
        if (course.curriculumSubjectId) {
          const topics = await getCoreTopicsByCurriculumSubject(course.curriculumSubjectId);
          setCoreTopics(topics);
        } else {
          setCoreTopics([]);
        }

        // EO hierarchy must use the course's subject, not the curriculum record's default subject.
        if (course.curriculumId && course.curriculumSubjectId) {
          const hierarchy = await getEOHierarchyByCurriculum(course.curriculumId, course.curriculumSubjectId);
          setEOHierarchy(hierarchy);
        } else {
          setEOHierarchy([]);
        }
      } catch (err: unknown) {
        console.error('Failed to load course data:', err);
        setError(describeCourseDataLoadFailure(err));
      }
    };

    loadDataForCourse();
  }, [course]);

  // Load Quiz Config when Tab changes to 4
  useEffect(() => {
    if (tabValue === 4 && courseId) {
      loadQuizConfig();
    }
  }, [tabValue, courseId]);

  const loadQuizConfig = async () => {
    if (!courseId) return;
    try {
      setLoadingConfig(true);
      const config = await getQuizConfig('COURSE', parseInt(courseId));
      // Check if config is "empty" (all fields null) or present
      if (config && config.maxQuestions != null) {
        setQuizConfig(config);
      } else {
        setQuizConfig(null);
      }
    } catch (err) {
      console.error("Failed to load quiz config", err);
      // Don't block UI, just set null
      setQuizConfig(null);
    } finally {
      setLoadingConfig(false);
    }
  };

  // Auto-populate structure from EOHierarchy if structure is empty (before draft rows exist)
  useEffect(() => {
    if (structureNodes.length === 0 && eoHierarchy?.length > 0 && !structureLoading && courseId) {
      const defaultNodes = buildCurriculumStructureFromEoHierarchy(eoHierarchy, parseInt(courseId, 10));
      console.log('[ContentManagement] Auto-populating structure from EO hierarchy', { areaCount: defaultNodes.length });
      if (defaultNodes.length > 0) {
        setStructureNodes(defaultNodes);
      }
    }
  }, [eoHierarchy, structureNodes.length, structureLoading, courseId]);

  // Reload books when view dialog opens
  useEffect(() => {
    if (viewDialogOpen) {
      loadBooks();
    }
  }, [viewDialogOpen]);

  const loadCourseLocalTopicIds = async (courseIdNum: number) => {
    try {
      const topics = await getCourseCustomTopics(courseIdNum);
      setCourseLocalTopicIds(new Set(topics.map((t) => t.id)));
    } catch (err) {
      console.error('[ContentManagement] Failed to load course custom topics', err);
      setCourseLocalTopicIds(new Set());
    }
  };

  const loadCourse = async () => {
    if (!courseId) return;
    try {
      const parsedCourseId = parseInt(courseId, 10);
      const data = await getCourseById(parsedCourseId);
      setCourse(data);
      await loadCourseLocalTopicIds(parsedCourseId);
    } catch (err: any) {
      console.error('Failed to load course:', err);
      setError(err.response?.data?.message || 'Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async () => {
    if (!courseId) return;
    try {
      const v = await getCourseStructureVersions(parseInt(courseId), selectedView?.id);
      setVersions(v);
      console.log('[ContentManagement] loadVersions', { count: v.length, versionIds: v.map(x => x.id) });
    } catch (err: any) {
      console.error('[ContentManagement] Failed to load versions:', err?.message ?? err, err);
    }
  };

  const loadStructure = async (localTopicIdsOverride?: Set<number>) => {
    if (!courseId) return;
    const loadGeneration = structureLoadGeneration.current;
    const localTopicIds = localTopicIdsOverride ?? courseLocalTopicIds;
    setStructureLoading(true);
    try {
      const viewId = selectedView?.id ?? undefined;
      const versionId = selectedVersionId ?? undefined;
      const parsedCourseId = parseInt(courseId, 10);
      console.log('[ContentManagement] loadStructure', { courseId, viewId, versionId, isDraft: !selectedVersionId });
      const nodes = await getCourseStructure(parsedCourseId, viewId, versionId);
      if (loadGeneration !== structureLoadGeneration.current) {
        console.log('[ContentManagement] loadStructure ignored (stale)', { courseId });
        return;
      }
      console.log('[ContentManagement] loadStructure result', { nodeCount: nodes.length, firstNodeId: nodes[0]?.id });

      const allowedTopicIds = collectCoreTopicIds(coreTopics);

      // Draft: merge persisted rows into curriculum scaffold so Area/Group/EO stay visible.
      // Published version view: show stored tree only (read-only).
      let displayNodes =
        !selectedVersionId && eoHierarchy.length > 0
          ? mergeCurriculumScaffoldWithPersistedNodes(
              buildCurriculumStructureFromEoHierarchy(eoHierarchy, parsedCourseId),
              nodes
            )
          : nodes;

      // Strip leaked topic refs from other subjects; keep core-library and course-local custom topics.
      displayNodes = filterTopicRefsByAllowedIds(displayNodes, allowedTopicIds, localTopicIds);

      setStructureNodes(displayNodes);
    } catch (err: any) {
      console.error('[ContentManagement] Failed to load course structure:', err?.message ?? err, err);
    } finally {
      if (loadGeneration === structureLoadGeneration.current) {
        setStructureLoading(false);
      }
    }
  };

  const handleRestoreVersion = async () => {
    if (!courseId || !selectedVersionId) return;
    if (!confirm('This will overwrite your current Draft with this Version. Continue?')) return;
    try {
      await restoreCourseStructureVersion(parseInt(courseId), selectedVersionId, selectedView?.id);
      alert('Version restored to Draft successfully!');
      setSelectedVersionId(null); // Switch to Draft
      loadStructure(); // Reload
    } catch (e: any) {
      setError('Failed to restore version: ' + e.message);
    }
  };

  const handleDeleteVersion = async (versionId: number) => {
    if (!courseId) return;
    if (!confirm('Delete this version? This cannot be undone.')) return;
    try {
      await deleteCourseStructureVersion(parseInt(courseId), versionId, selectedView?.id);
      loadVersions(); // Refresh list
      if (selectedVersionId === versionId) {
        setSelectedVersionId(null); // Switch to Draft if deleted active view
      }
    } catch (e: any) {
      alert('Failed to delete version: ' + e.message);
    }
  };



  const loadCourseTopics = async () => {
    // With the legacy topic service removed, we now derive course topics from the core subject topics.
    // This ensures consistency with the curriculum structure.
    if (coreTopics.length === 0) {
      setCourseTopics([]);
      return;
    }
    const transformedTopics = coreTopics.map(topic => ({
      id: topic.id,
      displayName: topic.title,
      coreTopicTitle: topic.title,
      description: topic.description,
      hasCustomContent: false,
      displayOrder: topic.sortOrder || 0,
      status: 'not_started',
      estimatedTimeMinutes: 0,
      defaultVisibleToStudents: topic.isActive ?? true,
    }));
    setCourseTopics(transformedTopics);
  };

  // Sync course topics whenever core topics change
  useEffect(() => {
    loadCourseTopics();
  }, [coreTopics]);

  const loadViews = async () => {
    if (!courseId) return;
    try {
      const viewsData = await getViewsByCourse(parseInt(courseId));
      setViews(viewsData);
      setSelectedView(prev => {
        if (viewsData.length === 0) return null;
        const retained = prev?.id
          ? viewsData.find((v: CourseViewDTO) => v.id === prev.id) ?? null
          : null;
        return retained ?? viewsData.find((v: CourseViewDTO) => v.isDefault) ?? viewsData[0] ?? null;
      });
      console.log('[ContentManagement] loadViews', { count: viewsData.length, activeVersionIds: viewsData.map((v: CourseViewDTO) => ({ viewId: v.id, activeVersionId: v.activeVersionId })) });
    } catch (err: any) {
      console.error('[ContentManagement] Failed to load views:', err?.message ?? err, err);
      setError(err?.message || 'Failed to load views');
    }
  };

  const loadBooks = async () => {
    try {
      const booksData = await getAllBooks();
      setBooks(booksData);
    } catch (err: any) {
      console.error('Failed to load books:', err);
      setError(err.message || 'Failed to load books');
    }
  };

  const loadMockResources = () => {
    // Mock resources - TODO: Replace with API call when resource management is implemented
    setResources([
      {
        id: 1,
        name: 'Introduction to Addition Video',
        resourceType: 'VIDEO',
        fileSize: 52428800,
        duration: 300,
        topicCount: 3,
      },
      {
        id: 2,
        name: 'Number Line Worksheet',
        resourceType: 'FILE',
        fileSize: 1024000,
        mimeType: 'application/pdf',
        topicCount: 5,
      },
      {
        id: 3,
        name: 'Interactive Number Game',
        resourceType: 'INTERACTIVE',
        externalUrl: 'https://example.com/game',
        topicCount: 2,
      },
    ]);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: any) => {
    setMenuAnchor(event.currentTarget);
    setMenuItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  const [expandedCoreTopics, setExpandedCoreTopics] = useState<Set<number>>(new Set());
  const [expandedCourseTopics, setExpandedCourseTopics] = useState<Set<number>>(new Set());
  const [expandedAreas, setExpandedAreas] = useState<Set<number>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const toggleCoreTopic = (topicId: number) => {
    const newExpanded = new Set(expandedCoreTopics);
    if (newExpanded.has(topicId)) {
      newExpanded.delete(topicId);
    } else {
      newExpanded.add(topicId);
    }
    setExpandedCoreTopics(newExpanded);
  };

  const toggleCourseTopic = (topicId: number) => {
    const newExpanded = new Set(expandedCourseTopics);
    if (newExpanded.has(topicId)) {
      newExpanded.delete(topicId);
    } else {
      newExpanded.add(topicId);
    }
    setExpandedCourseTopics(newExpanded);
  };

  const toggleArea = (areaId: number) => {
    const newExpanded = new Set(expandedAreas);
    if (newExpanded.has(areaId)) {
      newExpanded.delete(areaId);
    } else {
      newExpanded.add(areaId);
    }
    setExpandedAreas(newExpanded);
  };

  const toggleGroup = (groupId: number) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const toggleStructureNode = (nodeId: number) => {
    const newExpanded = new Set(expandedNodeIds);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodeIds(newExpanded);
  };



  const handleAddWrapper = async () => {
    if (!courseId) return;
    const name = prompt("Enter Wrapper Name (e.g., 'Module 1'):", "New Module");
    if (!name) return;
    addChildWrapperLocally(null, name);
  };

  const canHaveStructureChildren = (node: CourseStructureNodeDTO) =>
    node.nodeType === 'WRAPPER' || node.nodeType === 'CURRICULUM_REF';

  const appendChildToStructureTree = (
    nodes: CourseStructureNodeDTO[],
    parentId: number,
    child: CourseStructureNodeDTO
  ): CourseStructureNodeDTO[] =>
    nodes.map((n) => {
      if (n.id === parentId) {
        const children = [...(n.children || []), child];
        return { ...n, children, isExpanded: true };
      }
      if (n.children?.length) {
        return { ...n, children: appendChildToStructureTree(n.children, parentId, child) };
      }
      return n;
    });

  const addChildWrapperLocally = (parent: CourseStructureNodeDTO | null, name: string) => {
    if (!courseId) return;
    const newWrapper: CourseStructureNodeDTO = {
      id: -(Date.now() + Math.floor(Math.random() * 1000)),
      courseId: parseInt(courseId),
      label: name,
      nodeType: 'WRAPPER',
      viewId: selectedView?.id || 0,
      parentId: parent?.id ?? 0,
      displayOrder: parent ? (parent.children?.length || 0) : structureNodes.length,
      isVisible: true,
      isExpanded: true,
      children: [],
      deleteEnabled: true,
      editEnabled: true,
    };

    if (parent) {
      setStructureNodes((prev) => appendChildToStructureTree(prev, parent.id, newWrapper));
      setExpandedNodeIds((prev) => new Set(prev).add(parent.id));
    } else {
      setStructureNodes((prev) => [...prev, newWrapper]);
    }
  };

  const openAddChildDialog = (parent: CourseStructureNodeDTO, mode: 'wrapper' | 'topic') => {
    setAddChildParent(parent);
    setAddChildMode(mode);
    setAddChildName(mode === 'wrapper' ? 'New Module' : 'New Topic');
    setAddChildDialogOpen(true);
  };

  const handleConfirmAddChild = async () => {
    if (!courseId || !addChildParent) return;
    const name = addChildName.trim();
    if (!name) return;

    if (selectedVersionId != null) {
      setError('Switch to Draft to add children.');
      return;
    }

    if (addChildMode === 'wrapper') {
      addChildWrapperLocally(addChildParent, name);
      setAddChildDialogOpen(false);
      return;
    }

    setOperationStatus('pending');
    setOperationMessage('Creating custom topic...');
    setError(null);
    try {
      const parentDbId = await ensureStructureNodePersisted(
        parseInt(courseId),
        selectedView?.id,
        addChildParent,
        structureNodes
      );
      const createdTopic = await createCourseCustomTopic(parseInt(courseId), { title: name });
      const updatedLocalIds = new Set(courseLocalTopicIds).add(createdTopic.id);
      setCourseLocalTopicIds(updatedLocalIds);
      await addCourseStructureNode(parseInt(courseId), selectedView?.id, {
        parentId: parentDbId,
        nodeType: 'TOPIC_REF',
        refId: createdTopic.id,
        label: name,
      });
      setOperationStatus('success');
      setOperationMessage('Custom topic added. Refreshing...');
      await loadCourseLocalTopicIds(parseInt(courseId, 10));
      await loadStructure(updatedLocalIds);
      setExpandedNodeIds((prev) => new Set(prev).add(addChildParent.id));
      setAddChildDialogOpen(false);
      setTimeout(() => { setOperationStatus('idle'); setOperationMessage(''); }, 3000);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to add custom topic';
      setOperationStatus('error');
      setOperationMessage(msg);
      setError(msg);
      setTimeout(() => { setOperationStatus('idle'); setOperationMessage(''); }, 6000);
    }
  };

  const renderCoreTopicTree = (topicNodes: any[], level: number = 0): React.ReactNode => {
    return topicNodes.map((topic) => {
      const hasChildren = topic.children && topic.children.length > 0;
      const isExpanded = expandedCoreTopics.has(topic.id);

      return (
        <Box key={topic.id} sx={{ pl: level * 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              p: 1,
              '&:hover': { bgcolor: 'action.hover' },
              cursor: hasChildren ? 'pointer' : 'grab',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mb: 0.5,
            }}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('type', 'TOPIC_LIBRARY_ITEM');
              e.dataTransfer.setData('topicId', topic.id.toString());
              e.dataTransfer.setData('title', topic.title);
              e.dataTransfer.setData('includeChildren', 'true');
              console.log('[ContentManagement] Drag start: topic from library', { topicId: topic.id, title: topic.title });
            }}
            onDragEnd={() => clearStructureDragOver()}
            onClick={() => hasChildren && toggleCoreTopic(topic.id)}
          >
            <Box display="flex" alignItems="center" gap={1} flex={1}>
              {hasChildren ? (
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation();
                  toggleCoreTopic(topic.id);
                }}>
                  {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              ) : (
                <Box sx={{ width: 40 }} />
              )}
              <DragIcon fontSize="small" color="action" />
              <FileIcon fontSize="small" color="action" />
              <Typography variant="body2">{topic.title}</Typography>
            </Box>
          </Box>
          {hasChildren && isExpanded && (
            <Box>
              {renderCoreTopicTree(topic.children, level + 1)}
            </Box>
          )}
        </Box>
      );
    });
  };

  const renderEOHierarchy = (): React.ReactNode => {
    if (eoHierarchy.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">
            No Experience Outcomes found for this curriculum subject.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
          Curriculum Structure (Experience Outcomes)
        </Typography>
        {eoHierarchy.map((area) => {
          const isAreaExpanded = expandedAreas.has(area.areaId);
          return (
            <Box key={area.areaId} sx={{ mb: 2 }}>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  p: 1,
                  bgcolor: 'primary.light',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'primary.main', color: 'white' },
                }}
                onClick={() => toggleArea(area.areaId)}
              >
                <IconButton size="small" sx={{ color: 'inherit' }}>
                  {isAreaExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
                <FolderIcon sx={{ mr: 1 }} />
                <Typography variant="body2" fontWeight="bold">
                  {area.areaName}
                </Typography>
                {area.areaDescription && (
                  <Typography variant="caption" sx={{ ml: 1, opacity: 0.8 }}>
                    - {area.areaDescription}
                  </Typography>
                )}
              </Box>
              {isAreaExpanded && area.groups && area.groups.length > 0 && (
                <Box sx={{ pl: 3, mt: 1 }}>
                  {area.groups.map((group) => {
                    const isGroupExpanded = expandedGroups.has(group.groupId);
                    return (
                      <Box key={group.groupId} sx={{ mb: 1 }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          sx={{
                            p: 0.75,
                            bgcolor: 'secondary.light',
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'secondary.main', color: 'white' },
                          }}
                          onClick={() => toggleGroup(group.groupId)}
                        >
                          <IconButton size="small" sx={{ color: 'inherit' }}>
                            {isGroupExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                          </IconButton>
                          <FolderOpenIcon sx={{ mr: 1, fontSize: 'small' }} />
                          <Typography variant="body2">
                            {group.groupName}
                          </Typography>
                        </Box>
                        {isGroupExpanded && group.experienceOutcomes && group.experienceOutcomes.length > 0 && (
                          <Box sx={{ pl: 3, mt: 0.5 }}>
                            {group.experienceOutcomes.map((eo) => (
                              <Box
                                key={eo.eoId}
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.setData('type', 'CURRICULUM_LIBRARY_ITEM');
                                  e.dataTransfer.setData('refId', eo.eoId.toString());
                                  e.dataTransfer.setData('label', `${eo.code}: ${eo.description}`);
                                }}
                                sx={{
                                  p: 0.5,
                                  mb: 0.5,
                                  bgcolor: 'grey.100',
                                  borderRadius: 0.5,
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'grab',
                                  '&:hover': { bgcolor: 'grey.200' }
                                }}
                              >
                                <FileIcon sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
                                <Typography variant="caption" fontWeight="bold">
                                  {eo.code}:
                                </Typography>
                                <Typography variant="caption" sx={{ ml: 0.5 }}>
                                  {eo.description}
                                </Typography>
                                {eo.level && (
                                  <Chip
                                    label={eo.level}
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 1, height: 18 }}
                                  />
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const handleDeleteNode = async (node: CourseStructureNodeDTO) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    if (!courseId) return;
    setOperationStatus('pending');
    setOperationMessage('Deleting topic from course structure...');
    setError(null);
    try {
      await deleteCourseStructureNode(parseInt(courseId), node.id);
      setOperationStatus('success');
      setOperationMessage('Topic deleted. Refreshing...');
      await loadStructure();
      setOperationMessage('Topic deleted successfully.');
      setTimeout(() => { setOperationStatus('idle'); setOperationMessage(''); }, 3000);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Delete failed';
      setOperationStatus('error');
      setOperationMessage(msg);
      setError(msg);
      console.error('[ContentManagement] deleteCourseStructureNode failed:', err?.response?.data ?? err?.message, err);
    }
  };

  /** Walk the in-memory tree to find the path from root to a node (used for virtual curriculum ids). */
  const findStructureNodePath = (
    nodes: CourseStructureNodeDTO[],
    targetId: number,
    path: CourseStructureNodeDTO[] = []
  ): CourseStructureNodeDTO[] | null => {
    for (const node of nodes) {
      const currentPath = [...path, node];
      if (node.id === targetId) return currentPath;
      if (node.children?.length) {
        const found = findStructureNodePath(node.children, targetId, currentPath);
        if (found) return found;
      }
    }
    return null;
  };

  const findStructureNodeById = (
    nodes: CourseStructureNodeDTO[],
    targetId: number
  ): CourseStructureNodeDTO | null => {
    for (const node of nodes) {
      if (node.id === targetId) return node;
      if (node.children?.length) {
        const found = findStructureNodeById(node.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  /**
   * Curriculum scaffolding uses temporary negative ids in the UI until persisted.
   * Before adding/moving under such a node, create its ancestor chain in draft storage.
   */
  const ensureStructureNodePersisted = async (
    courseIdNum: number,
    viewId: number | undefined,
    targetNode: CourseStructureNodeDTO,
    nodes: CourseStructureNodeDTO[]
  ): Promise<number> => {
    if (targetNode.id > 0) return targetNode.id;

    const path = findStructureNodePath(nodes, targetNode.id);
    if (!path) {
      throw new Error('Could not locate the target node in the course structure.');
    }

    let parentDbId: number | undefined;
    const idReplacements = new Map<number, number>();

    for (const node of path) {
      if (node.id > 0) {
        parentDbId = node.id;
        continue;
      }
      const created = await addCourseStructureNode(courseIdNum, viewId, {
        parentId: parentDbId,
        nodeType: node.nodeType,
        refId: node.refId,
        label: node.label,
      });
      idReplacements.set(node.id, created.id);
      parentDbId = created.id;
    }

    if (parentDbId == null) {
      throw new Error('Failed to persist parent structure node.');
    }

    if (idReplacements.size > 0) {
      setStructureNodes((prev) => applyNodeIdReplacements(prev, idReplacements));
    }

    return parentDbId;
  };

  /** Handle drop onto a specific structure node (or delegate from root when hover target is set). */
  const handleStructureNodeDrop = async (
    e: React.DragEvent,
    node: CourseStructureNodeDTO
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const type = e.dataTransfer.getData('type');
    console.log('[ContentManagement] Node drop', { type, targetNodeId: node.id, selectedVersionId });
    if (selectedVersionId != null) {
      console.warn('[ContentManagement] Node drop ignored: viewing a version (read-only)');
      setError('Switch to Draft to add or move topics.');
      return;
    }

    if (type === 'STRUCTURE_NODE') {
      const draggedId = parseInt(e.dataTransfer.getData('nodeId'));
      if (draggedId === node.id) return;
      if (!courseId) return;

      setOperationStatus('pending');
      setOperationMessage('Moving topic...');
      setError(null);
      try {
        const parentDbId = await ensureStructureNodePersisted(
          parseInt(courseId),
          selectedView?.id,
          node,
          structureNodes
        );
        await moveCourseStructureNode(parseInt(courseId), draggedId, { newParentId: parentDbId });
        setOperationStatus('success');
        setOperationMessage('Moved. Refreshing...');
        await loadStructure();
        setOperationMessage('Topic moved successfully.');
        setTimeout(() => { setOperationStatus('idle'); setOperationMessage(''); }, 3000);
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || 'Failed to move topic';
        setOperationStatus('error');
        setOperationMessage(msg);
        setError(msg);
        console.error('[ContentManagement] moveCourseStructureNode (reparent) failed:', err?.response?.data ?? err?.message, err);
        setTimeout(() => { setOperationStatus('idle'); setOperationMessage(''); }, 6000);
      }
    } else if (type === 'TOPIC_LIBRARY_ITEM') {
      const refId = parseInt(e.dataTransfer.getData('topicId'));
      const label = e.dataTransfer.getData('title');
      console.log('[ContentManagement] Node drop: topic onto node', { refId, label, targetNodeId: node.id });

      if (getAllTopicRefIdsInTree(structureNodes).has(refId)) {
        setError('This topic is already in the course structure. Each topic can only appear once per course.');
        return;
      }
      if (!courseId) return;

      setOperationStatus('pending');
      setOperationMessage('Adding topic as child...');
      setError(null);
      try {
        const parentDbId = await ensureStructureNodePersisted(
          parseInt(courseId),
          selectedView?.id,
          node,
          structureNodes
        );
        await addCourseStructureNode(parseInt(courseId), selectedView?.id, {
          parentId: parentDbId,
          nodeType: 'TOPIC_REF',
          refId,
          label,
        });
        setOperationStatus('success');
        setOperationMessage('Topic added. Refreshing...');
        await loadStructure();
        setOperationMessage('Topic added as child successfully.');
        setTimeout(() => { setOperationStatus('idle'); setOperationMessage(''); }, 3000);
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || 'Failed to add topic as child';
        setOperationStatus('error');
        setOperationMessage(msg);
        setError(msg);
        console.error('[ContentManagement] addCourseStructureNode (child) failed:', err?.response?.data ?? err?.message, err);
        setTimeout(() => { setOperationStatus('idle'); setOperationMessage(''); }, 6000);
      }
    } else if (type === 'CURRICULUM_LIBRARY_ITEM') {
      console.warn("Curriculum items cannot be manually dragged in this version.");
    }
  };

  /** Collect all topic refIds (TOPIC_REF) in the tree so we can block duplicate topics in the same course. */
  const getAllTopicRefIdsInTree = (nodes: CourseStructureNodeDTO[]): Set<number> => {
    const refIds = new Set<number>();
    const visit = (list: CourseStructureNodeDTO[] | undefined) => {
      if (!list) return;
      for (const n of list) {
        if (n.nodeType === 'TOPIC_REF' && n.refId != null) refIds.add(n.refId);
        if (n.children?.length) visit(n.children);
      }
    };
    visit(nodes);
    return refIds;
  };

  const handlePublish = async () => {
    if (!courseId) return;
    if (!confirm('This will update the course structure for students. Continue?')) return;
    setOperationStatus('pending');
    setOperationMessage('Saving and publishing course structure...');
    setError(null);
    try {
      await saveCourseStructure(
        parseInt(courseId),
        structureNodes,
        selectedView?.id
      );
      setOperationMessage('Publishing to students...');
      await publishCourseStructure(parseInt(courseId), selectedView?.id);
      await loadVersions();
      await loadViews();
      loadStructure();
      setError('');
      setOperationStatus('success');
      setOperationMessage('Published. Select the new version and click "Set as Active" to show it to students.');
      setTimeout(() => { setOperationStatus('idle'); setOperationMessage(''); }, 5000);
    } catch (e: any) {
      const msg = 'Failed to publish: ' + (e?.response?.data?.message || e?.message || 'Unknown error');
      setOperationStatus('error');
      setOperationMessage(msg);
      setError(msg);
      console.error('Publish failed:', e);
      setTimeout(() => { setOperationStatus('idle'); setOperationMessage(''); }, 8000);
    }
  };

  /* New Graph Tree Renderer */
  const renderStructureTree = (nodes: CourseStructureNodeDTO[], level = 0): React.ReactNode => {
    if (!nodes || nodes.length === 0) return null;

    return nodes.map((node) => {
      if (!node) return null;
      const isExpanded = expandedNodeIds.has(node.id) || node.isExpanded;
      const hasChildren = node.children && node.children.length > 0;

      const getIcon = () => {
        if (node.nodeType === 'WRAPPER') return <FolderOpenIcon color="primary" />;
        if (node.nodeType === 'CURRICULUM_REF') return <School color="secondary" />;
        return <FileIcon color="action" />;
      };

      const handleNodeDrop = async (e: React.DragEvent) => {
        clearStructureDragOver();
        await handleStructureNodeDrop(e, node);
      };

      return (
        <Box
          key={node.id}
          sx={{ ml: level * 2, mb: 0.5 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1,
              border: '2px solid',
              borderColor: dragOverNodeId === node.id ? 'primary.main' : 'divider', // Strong highlight
              borderRadius: 1,
              bgcolor: dragOverNodeId === node.id ? 'action.hover' : 'background.paper', // Background change
              '&:hover': { borderColor: 'primary.light' },
              transition: 'all 0.2s',
              position: 'relative',
              boxShadow: dragOverNodeId === node.id ? 2 : 0
            }}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('type', 'STRUCTURE_NODE');
              e.dataTransfer.setData('nodeId', node.id.toString());
              e.stopPropagation();
              clearStructureDragOver();
              console.log('[ContentManagement] Drag start: structure node', { nodeId: node.id, nodeType: node.nodeType });
            }}
            onDragEnd={() => clearStructureDragOver()}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const current = dragOverTargetRef.current;
              if (!current || level >= current.level) {
                dragOverTargetRef.current = { id: node.id, level };
                setDragOverNodeId(node.id);
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDragLeave={(e) => {
              e.stopPropagation();
              const related = e.relatedTarget as Node | null;
              if (related && e.currentTarget.contains(related)) {
                return;
              }
              if (dragOverTargetRef.current?.id === node.id) {
                clearStructureDragOver();
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              clearStructureDragOver();
              handleNodeDrop(e);
            }}
          >
            {/* Drop Target Indicator Label */}
            {dragOverNodeId === node.id && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -10,
                  right: 10,
                  bgcolor: 'primary.main',
                  color: 'white',
                  px: 1,
                  borderRadius: 1,
                  fontSize: '0.6rem',
                  fontWeight: 'bold',
                  zIndex: 1
                }}
              >
                DROP INSIDE: {node.label.substring(0, 15)}...
              </Box>
            )}

            {hasChildren || node.nodeType === 'WRAPPER' ? (
              <IconButton size="small" onClick={() => toggleStructureNode(node.id)}>
                {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </IconButton>
            ) : <Box sx={{ width: 28 }} />}

            {/* Red Delete Action Removed from here */}

            <Box sx={{ mr: 1, display: 'flex' }}>{getIcon()}</Box>

            <Box flex={1}>
              <Typography variant="body2" component="span" fontWeight={node.nodeType === 'WRAPPER' ? 'bold' : 'normal'}>
                {node.label}
              </Typography>
              {node.nodeType === 'TOPIC_REF' && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (Topic)
                </Typography>
              )}
            </Box>

            {/* Actions */}
            <Box>
              {canHaveStructureChildren(node) && selectedVersionId == null && (
                <Tooltip title="Add child">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuAnchor(e.currentTarget);
                      setMenuItem({ type: 'ADD_CHILD', node });
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={node.isVisible ? "Hide Node" : "Show Node"}>
                <IconButton size="small" onClick={async (e) => {
                  e.stopPropagation();

                  // Frontend-only visibility toggle
                  const toggleVisibility = (nodes: CourseStructureNodeDTO[]): CourseStructureNodeDTO[] => {
                    return nodes.map(n => {
                      if (n.id === node.id) {
                        return { ...n, isVisible: !n.isVisible };
                      }
                      if (n.children) {
                        return { ...n, children: toggleVisibility(n.children) };
                      }
                      return n;
                    });
                  };
                  setStructureNodes(prev => toggleVisibility(prev));
                }}>
                  {node.isVisible ? <VisibilityIcon fontSize="small" color="primary" /> : <VisibilityOffIcon fontSize="small" color="error" />}
                </IconButton>
              </Tooltip>

              {(node.editEnabled !== false && node.nodeType !== 'CURRICULUM_REF') && (
                <Tooltip title="Edit / Rename">
                  <IconButton size="small" onClick={(e) => {
                    e.stopPropagation();
                    const newName = prompt("Rename node:", node.label);
                    if (newName) {
                      updateCourseStructureNode(parseInt(courseId || '0'), node.id, { label: newName })
                        .then(loadStructure);
                    }
                  }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Render Delete button for all nodes except curriculum ref (show for root-level and children) */}
              {node.nodeType !== 'CURRICULUM_REF' && (node.deleteEnabled !== false) && (
                <Tooltip title="Delete Node">
                  <IconButton
                    size="small"
                    color="default"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteNode(node);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Children */}
          {isExpanded && node.children && (
            // Fix recursive call: defined variable name must match 
            <Box>
              {renderStructureTree(node.children, level + 1)}
            </Box>
          )}
        </Box>
      );
    });
  };

  const renderCourseTopics = (): React.ReactNode => {
    if (courseTopics.length === 0) {
      return null;
    }

    return courseTopics.map((courseTopic, index) => {
      const isCustomName = courseTopic.displayName !== courseTopic.coreTopicTitle;

      return (
        <Box
          key={courseTopic.id}
          sx={{
            p: 2,
            mb: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            cursor: 'grab',
            transition: 'transform 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
            },
          }}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('sourceIndex', index.toString());
            e.dataTransfer.setData('type', 'COURSE_TOPIC_REORDER');
          }}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation(); // Prevent bubbling to parent drop zone
            const type = e.dataTransfer.getData('type');
            if (type === 'COURSE_TOPIC_REORDER') {
              const sourceIndex = parseInt(e.dataTransfer.getData('sourceIndex'));
              if (isNaN(sourceIndex) || sourceIndex === index) return;

              // Handle reorder locally
              const newTopics = [...courseTopics];
              const [movedItem] = newTopics.splice(sourceIndex, 1);
              newTopics.splice(index, 0, movedItem);
              setCourseTopics(newTopics);

              // TODO: Call backend to persist order (update displayOrder for all affected topics)
              // For now we just update UI
            }
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1} flex={1}>
              <DragIcon fontSize="small" color="action" />
              <Typography variant="body2" fontWeight={isCustomName ? 'bold' : 'normal'}>
                {courseTopic.displayName}
              </Typography>
              {isCustomName && (
                <Chip
                  label="Renamed"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
              {courseTopic.coreTopicTitle !== courseTopic.displayName && (
                <Typography variant="caption" color="text.secondary">
                  (Core: {courseTopic.coreTopicTitle})
                </Typography>
              )}
              {courseTopic.hasCustomContent && (
                <Chip label="Custom Content" size="small" color="info" />
              )}
            </Box>
            <Box>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedTopic(courseTopic);
                  // Fix: Open the correct dialog for metadata editing (Title, Description, etc.)
                  setTopicDialogOpen(true);
                }}
                title="Edit Details"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, courseTopic)}
                title="More options"
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      );
    });
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/admin/courses')}
        >
          Courses
        </Link>
        <Typography color="text.primary">
          {course?.name || 'Course'} - Content Management
        </Typography>
      </Breadcrumbs>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            Content Management - {course?.name || 'Course'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage course content, topics, views, books, and resources
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/courses')}
        >
          Back to Courses
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Views" icon={<ViewIcon />} iconPosition="start" />
          <Tab label="Topics" icon={<FolderIcon />} iconPosition="start" />
          <Tab label="Books" icon={<BookIcon />} iconPosition="start" />
          <Tab label="Resources" icon={<ResourceIcon />} iconPosition="start" />
          <Tab label="Configuration" icon={<SettingsIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Views Tab - Now First */}
      <TabPanel value={tabValue} index={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Course Views</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={async () => {
              setSelectedView(null);
              setViewType('DEFAULT');
              setSelectedBookId(null);
              setViewFormData({ name: '', isDefault: false });
              // Reload books to ensure we have the latest list
              await loadBooks();
              setViewDialogOpen(true);
            }}
          >
            Create View
          </Button>
        </Box>
        <Grid container spacing={3}>
          {views.map((view) => (
            <Grid size={{ xs: 12, md: 6 }} key={view.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Box>
                      <Typography variant="h6">{view.name}</Typography>
                      <Chip
                        label={view.viewType}
                        size="small"
                        color={view.isDefault ? 'primary' : 'default'}
                        sx={{ mt: 1 }}
                      />
                      {view.isDefault && (
                        <Chip label="Default" size="small" color="primary" sx={{ ml: 1, mt: 1 }} />
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, view)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {view.topicCount} topics mapped
                  </Typography>
                  {view.bookName && (
                    <Typography variant="body2" color="text.secondary">
                      Book: {view.bookName}
                    </Typography>
                  )}
                  <Box mt={2} display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        // Switch to Topics tab for this view
                        setSelectedView(view);
                        setTabValue(1);
                      }}
                    >
                      Topic Management
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      color="secondary"
                      startIcon={<School />}
                      onClick={() => {
                        // Open Student View in new tab (Preview Mode)
                        window.open(`/admin/preview/courses/${courseId}?viewId=${view.id}`, '_blank');
                      }}
                    >
                      Student View
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Topics Tab */}
      <TabPanel value={tabValue} index={1}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Core Topics</strong> are managed in Topic Management (System Admin only).
            Select topics from the library below and customize them for this course.
            Course-level changes (reshuffle, rename, delete) only affect this course.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {/* Core Topic Library (Left Column) */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 2, height: 'calc(100vh - 140px)', overflow: 'auto' }}>
              <Typography variant="h6" gutterBottom>Core Topic Library</Typography>
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                Drag topics from here into the Course Structure on the right.
              </Alert>
              <Box sx={{ mb: 3 }}>
                {coreTopics.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No Core Topics found.
                  </Typography>
                ) : (
                  renderCoreTopicTree(coreTopics)
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Course Structure (Right Column) */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 2, height: '100%', minHeight: 600 }}>
              {/* Operation status: full-width row so user always sees backend call result */}
              {operationStatus !== 'idle' && (
                <Alert
                  severity={operationStatus === 'error' ? 'error' : operationStatus === 'success' ? 'success' : 'info'}
                  sx={{ mb: 2 }}
                  onClose={() => { setOperationStatus('idle'); setOperationMessage(''); }}
                >
                  {operationStatus === 'pending' && <CircularProgress size={18} sx={{ mr: 1.5, verticalAlign: 'middle' }} />}
                  {operationMessage}
                </Alert>
              )}

              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="h6">Course Structure</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Editing: {selectedView ? selectedView.name : 'Default View'}
                  </Typography>
                </Box>

                <Box flex={1} display="flex" justifyContent="center">
                  <FormControl size="small" sx={{ minWidth: 200, maxWidth: 300 }}>
                    <InputLabel id="content-version-label">Version</InputLabel>
                    <Select
                      labelId="content-version-label"
                      value={selectedVersionId !== null ? selectedVersionId : 'DRAFT'}
                      label="Version"
                      onChange={(e) => {
                        const val = e.target.value;
                        const isDraft = val === 'DRAFT' || val === '';
                        const versionId = isDraft ? null : Number(val);
                        console.log('[ContentManagement] Version changed', { from: selectedVersionId, to: versionId, isDraft });
                        setSelectedVersionId(versionId);
                      }}
                    >
                      <MenuItem value="DRAFT"><em>Draft (Editable)</em></MenuItem>
                      {versions.map(v => {
                        const activeVersionId = selectedView?.activeVersionId ?? views.find(vw => vw.isDefault)?.activeVersionId ?? views[0]?.activeVersionId;
                        const isActive = activeVersionId != null && v.id === activeVersionId;
                        if (versions.length > 0 && activeVersionId == null && selectedView != null) {
                          console.warn('[ContentManagement] activeVersionId missing on view', { viewId: selectedView.id, viewName: selectedView.name });
                        }
                        return (
                          <MenuItem key={v.id} value={v.id}>
                            {v.versionNumber} ({new Date(v.createdAt).toLocaleDateString()}){isActive ? ' — Active (students see this)' : ''}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>

                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddWrapper}
                    disabled={selectedVersionId !== null}
                  >
                    Add Wrapper
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handlePublish}
                    color="primary"
                    disabled={selectedVersionId !== null}
                  >
                    Publish Changes
                  </Button>

                  {selectedVersionId ? (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        disabled={
                          (selectedView?.activeVersionId ?? views.find(v => v.isDefault)?.activeVersionId ?? views[0]?.activeVersionId) === selectedVersionId
                        }
                        onClick={async () => {
                          if (!courseId || !selectedView?.id) return;
                          try {
                            await setActiveVersion(parseInt(courseId), selectedView.id, selectedVersionId);
                            await loadViews();
                            setError('');
                          } catch (e: any) {
                            setError(e?.response?.data?.message || e?.message || 'Failed to set active version');
                          }
                        }}
                      >
                        Set as Active
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleDeleteVersion(selectedVersionId)}
                      >
                        Delete Version
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={async () => {
                        if (!courseId) return;
                        if (confirm('Reset draft structure? This clears all draft nodes for this view.')) {
                          try {
                            setOperationStatus('pending');
                            await deleteCourseStructure(parseInt(courseId), selectedView?.id);
                            setSelectedVersionId(null);
                            structureLoadGeneration.current += 1;
                            setStructureNodes([]);
                            await loadStructure();
                            setOperationStatus('success');
                            setOperationMessage('Draft structure reset.');
                          } catch (e: any) {
                            console.error(e);
                            setOperationStatus('error');
                            setError('Failed to reset structure');
                          }
                        }
                      }}
                    >
                      Reset
                    </Button>
                  )}
                </Box>
              </Box>

              {selectedVersionId && (
                <Alert severity="info" action={
                  <Button color="inherit" size="small" onClick={handleRestoreVersion}>
                    Make this my Draft
                  </Button>
                } sx={{ mb: 2 }}>
                  You are viewing a published version <strong>(Read-Only)</strong>. Drag-and-drop is disabled. Switch to <strong>Draft</strong> in the Version dropdown to add or move topics.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" mb={2}>
                Build your custom course tree. Create wrappers (Folders) to organize.
                Remember to <strong>Publish</strong> to update the student view.
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  minHeight: 500,
                  border: structureNodes.length === 0 ? '2px dashed' : '1px solid',
                  borderColor: 'grey.300',
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={async (e) => {
                  e.preventDefault();
                  const hoverTarget = dragOverTargetRef.current;
                  clearStructureDragOver();
                  const type = e.dataTransfer.getData('type');
                  console.log('[ContentManagement] Root drop', { type, courseId, selectedVersionId, selectedViewId: selectedView?.id, hoverTarget });
                  if (!courseId) {
                    console.warn('[ContentManagement] Root drop ignored: no courseId');
                    return;
                  }
                  if (selectedVersionId != null) {
                    console.warn('[ContentManagement] Root drop ignored: viewing a version (read-only). Switch to Draft to add topics.');
                    setError('Switch to Draft to add or move topics. You are currently viewing a published version (read-only).');
                    return;
                  }

                  // Drop released on container padding but cursor was over a node — route to that node.
                  if (hoverTarget && (type === 'TOPIC_LIBRARY_ITEM' || type === 'STRUCTURE_NODE')) {
                    const targetNode = findStructureNodeById(structureNodes, hoverTarget.id);
                    if (targetNode) {
                      console.log('[ContentManagement] Root drop redirected to hover target', { targetNodeId: targetNode.id });
                      await handleStructureNodeDrop(e, targetNode);
                      return;
                    }
                  }

                  if (type === 'STRUCTURE_NODE') {
                    const draggedId = parseInt(e.dataTransfer.getData('nodeId'));
                    console.log('[ContentManagement] Root drop: move node to root', { draggedId });
                    setOperationStatus('pending');
                    setOperationMessage('Moving topic...');
                    setError(null);
                    try {
                      await moveCourseStructureNode(parseInt(courseId), draggedId, {
                        newParentId: undefined
                      });
                      setOperationStatus('success');
                      setOperationMessage('Moved. Refreshing...');
                      await loadStructure();
                      setOperationMessage('Topic moved.');
                      setTimeout(() => { setOperationStatus('idle'); setOperationMessage(''); }, 3000);
                    } catch (err: any) {
                      const msg = err?.response?.data?.message || err?.message || 'Failed to move node';
                      setOperationStatus('error');
                      setOperationMessage(msg);
                      setError(msg);
                      console.error('[ContentManagement] moveCourseStructureNode failed:', err?.response?.data ?? err?.message, err);
                      setTimeout(() => { setOperationStatus('idle'); setOperationMessage(''); }, 6000);
                    }
                  } else if (type === 'TOPIC_LIBRARY_ITEM') {
                    const topicId = parseInt(e.dataTransfer.getData('topicId'), 10);
                    const title = e.dataTransfer.getData('title') || '';
                    console.log('[ContentManagement] Root drop: add topic', { topicId, title });
                    if (isNaN(topicId)) {
                      console.warn('[ContentManagement] Root drop: invalid topicId', e.dataTransfer.getData('topicId'));
                      return;
                    }
                    if (getAllTopicRefIdsInTree(structureNodes).has(topicId)) {
                      setError('This topic is already in the course structure. Each topic can only appear once per course.');
                      return;
                    }
                    setOperationStatus('pending');
                    setOperationMessage('Adding topic to course structure...');
                    setError(null);
                    try {
                      await addCourseStructureNode(parseInt(courseId), selectedView?.id, {
                        nodeType: 'TOPIC_REF',
                        refId: topicId,
                        label: title,
                      });
                      setError('');
                      setOperationStatus('success');
                      setOperationMessage('Topic added. Refreshing...');
                      await loadStructure();
                      setOperationMessage('Topic added successfully.');
                      setTimeout(() => { setOperationStatus('idle'); setOperationMessage(''); }, 3000);
                    } catch (err: any) {
                      const msg = err?.response?.data?.message || err?.message || 'Failed to add topic';
                      setOperationStatus('error');
                      setOperationMessage(msg);
                      setError(msg);
                      console.error('[ContentManagement] addCourseStructureNode failed:', err?.response?.data ?? err?.message, err);
                      setTimeout(() => { setOperationStatus('idle'); setOperationMessage(''); }, 6000);
                    }
                  } else {
                    console.log('[ContentManagement] Root drop: unhandled type', type);
                  }
                }}
              >
                {structureLoading ? (
                  <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                ) : structureNodes.length === 0 ? (
                  <Box textAlign="center" py={8}>
                    <DragIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary" mb={1}>
                      Course Structure Empty
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {renderStructureTree(structureNodes)}
                  </Box>
                )}
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Books Tab - Shows only Book type views */}
      <TabPanel value={tabValue} index={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Book-Based Views</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedBook(null);
              setBookFormData({
                title: '',
                publisher: '',
                authors: '',
                isbn: '',
                edition: '',
                year: undefined,
                coverImageUrl: '',
                description: '',
              });
              setBookDialogOpen(true);
            }}
          >
            Add Book
          </Button>
        </Box>
        <Grid container spacing={3}>
          {views.filter(v => v.viewType === 'BOOK_BASED').map((view) => (
            <Grid size={{ xs: 12, md: 6 }} key={view.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Box>
                      <Typography variant="h6">{view.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {view.bookName}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, view)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {view.topicCount} topics mapped
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 2 }}
                    onClick={() => {
                      // Switch to Topics tab for this view
                      setSelectedView(view);
                      setTabValue(1);
                    }}
                  >
                    Manage Chapters
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Resources Tab */}
      <TabPanel value={tabValue} index={3}>
        {/* ... Resources Tab Content ... */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Resource Library</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedResource(null);
              setResourceDialogOpen(true);
            }}
          >
            Add Resource
          </Button>
        </Box>
        <List>
          {resources.map((resource) => (
            <React.Fragment key={resource.id}>
              <ListItem>
                <ListItemIcon>
                  {resource.resourceType === 'VIDEO' && <VideoLibrary />}
                  {resource.resourceType === 'FILE' && <PictureAsPdf />}
                  {resource.resourceType === 'IMAGE' && <Image />}
                  {resource.resourceType === 'INTERACTIVE' && <LinkIcon />}
                  {!['VIDEO', 'FILE', 'IMAGE', 'INTERACTIVE'].includes(resource.resourceType) && <ResourceIcon />}
                </ListItemIcon>
                <ListItemText
                  primary={resource.name}
                  secondary={
                    <Box>
                      <Typography variant="caption" display="block">
                        Type: {resource.resourceType}
                      </Typography>
                      {resource.fileSize && (
                        <Typography variant="caption" display="block">
                          Size: {(resource.fileSize / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      )}
                      {resource.duration && (
                        <Typography variant="caption" display="block">
                          Duration: {Math.floor(resource.duration / 60)}:{(resource.duration % 60).toString().padStart(2, '0')}
                        </Typography>
                      )}
                      <Typography variant="caption" display="block">
                        Used in {resource.topicCount} topics
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={(e) => handleMenuOpen(e, resource)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>
      </TabPanel>

      {/* Configuration Tab */}
      <TabPanel value={tabValue} index={4}>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h5" gutterBottom>Course Configuration</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Manage default settings for all topics and quizzes within this course. These settings can be overridden at the topic level.
          </Typography>


          {courseId && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                Quiz Configuration
              </Typography>

              {loadingConfig ? (
                <CircularProgress size={20} />
              ) : quizConfig ? (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Active Configuration
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {quizConfig.maxQuestions} Questions • {quizConfig.timeLimitMinutes} Mins • {quizConfig.minPassPercent}% to Pass
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} sx={{ textAlign: 'right' }}>
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => setConfigModalOpen(true)}
                      >
                        Manage Quiz Configuration
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              ) : (
                <Alert severity="info" action={
                  <Button color="inherit" size="small" onClick={() => setConfigModalOpen(true)}>
                    Add Configuration
                  </Button>
                }>
                  No specific quiz configuration exists for this course. System defaults or Subject settings will apply.
                </Alert>
              )}

              <QuizConfigurationModal
                open={configModalOpen}
                onClose={() => {
                  setConfigModalOpen(false);
                  loadQuizConfig(); // Refresh on close
                }}
                level="COURSE"
                entityId={parseInt(courseId)}
                autoEdit={!quizConfig}
              />
            </Box>
          )}
        </Box>
      </TabPanel>

      {/* Course Topic Customization Dialog */}
      <Dialog open={topicDialogOpen} onClose={() => setTopicDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTopic ? 'Customize Course Topic' : 'Select Core Topics'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {selectedTopic ? (
              <>
                <Alert severity="info">
                  <Typography variant="body2">
                    Core Topic: <strong>{selectedTopic.coreTopicTitle || selectedTopic.title}</strong>
                  </Typography>
                  <Typography variant="caption">
                    Changes here only affect this course. Core topic remains unchanged.
                  </Typography>
                </Alert>
                <TextField
                  label="Display Name (Course-Specific)"
                  fullWidth
                  defaultValue={selectedTopic.displayName || selectedTopic.title || ''}
                  helperText="This name will be shown to students in this course"
                />
                <TextField label="Display Order" type="number" fullWidth defaultValue={selectedTopic.displayOrder || ''} />
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" defaultValue={selectedTopic.status || 'ACTIVE'}>
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                    <MenuItem value="DRAFT">Draft</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Estimated Time Override (Minutes)"
                  type="number"
                  fullWidth
                  defaultValue={selectedTopic.estimatedTimeMinutes || ''}
                  helperText="Leave empty to use core topic's estimated time"
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Use drag-and-drop from the core topic library to add topics to this course.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTopicDialogOpen(false)}>Cancel</Button>
          {selectedTopic && (
            <Button variant="contained" onClick={() => {
              // Handle save
              setTopicDialogOpen(false);
            }}>
              Save Changes
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => {
        setViewDialogOpen(false);
        setViewType('DEFAULT');
        setSelectedBookId(null);
        setSelectedView(null);
        setViewFormData({ name: '', isDefault: false });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedView ? 'Edit View' : 'Create View'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="View Name"
              fullWidth
              required
              value={viewFormData.name}
              onChange={(e) => setViewFormData({ ...viewFormData, name: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>View Type</InputLabel>
              <Select
                label="View Type"
                value={selectedView ? selectedView.viewType : viewType}
                onChange={(e) => {
                  const newViewType = e.target.value as 'DEFAULT' | 'BOOK_BASED';
                  if (selectedView) {
                    setSelectedView({ ...selectedView, viewType: newViewType });
                  } else {
                    setViewType(newViewType);
                  }
                  if (newViewType === 'DEFAULT') {
                    setSelectedBookId(null);
                  }
                }}
              >
                <MenuItem value="DEFAULT">Default View</MenuItem>
                <MenuItem value="BOOK_BASED">Book-Based View</MenuItem>
              </Select>
            </FormControl>
            {/* Book dropdown - shown only when BOOK_BASED is selected */}
            {(selectedView?.viewType === 'BOOK_BASED' || viewType === 'BOOK_BASED') && (
              <FormControl fullWidth>
                <InputLabel>Book</InputLabel>
                <Select
                  label="Book"
                  value={selectedView?.bookId || selectedBookId || ''}
                  onChange={async (e) => {
                    if (e.target.value === '__add_new__') {
                      // Store current view dialog state
                      const currentViewType = selectedView?.viewType || viewType;
                      setViewDialogOpen(false);
                      setBookDialogOpen(true);
                      // After book creation, we'll reopen the view dialog
                    } else {
                      const bookId = e.target.value as number;
                      if (selectedView) {
                        setSelectedView({ ...selectedView, bookId });
                      } else {
                        setSelectedBookId(bookId);
                      }
                    }
                  }}
                >
                  {books.map((book) => (
                    <MenuItem key={book.id} value={book.id}>
                      {book.title} - {book.publisher}
                    </MenuItem>
                  ))}
                  <MenuItem value="__add_new__">
                    <Box display="flex" alignItems="center" gap={1}>
                      <AddIcon fontSize="small" />
                      Add New Book
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={viewFormData.isDefault}
                  onChange={(e) => setViewFormData({ ...viewFormData, isDefault: e.target.checked })}
                />
              }
              label="Set as Default View"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setViewDialogOpen(false);
            setViewType('DEFAULT');
            setSelectedBookId(null);
            setSelectedView(null);
            setViewFormData({ name: '', isDefault: false });
          }}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            if (!courseId) return;
            try {
              if (!viewFormData.name.trim()) {
                setError('View name is required');
                return;
              }

              const currentViewType = selectedView?.viewType || viewType;

              // Validate book selection for BOOK_BASED views
              if (currentViewType === 'BOOK_BASED' && !selectedView?.bookId && !selectedBookId) {
                setError('Please select a book for book-based views');
                return;
              }

              const viewData: CourseViewDTO = {
                name: viewFormData.name.trim(),
                viewType: currentViewType,
                bookId: currentViewType === 'BOOK_BASED' ? (selectedView?.bookId || selectedBookId || undefined) : undefined,
                isDefault: viewFormData.isDefault,
                courseId: parseInt(courseId),
              };

              if (selectedView?.id) {
                await updateView(parseInt(courseId), selectedView.id, viewData);
              } else {
                await createView(parseInt(courseId), viewData);
              }

              await loadViews();
              setViewDialogOpen(false);
              setViewType('DEFAULT');
              setSelectedBookId(null);
              setSelectedView(null);
              setViewFormData({ name: '', isDefault: false });
            } catch (err: any) {
              console.error('Failed to save view:', err);
              setError(err.response?.data?.message || 'Failed to save view');
            }
          }}>
            {selectedView ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Book Dialog */}
      <Dialog open={bookDialogOpen} onClose={() => {
        setBookDialogOpen(false);
        setSelectedBook(null);
        setBookFormData({
          title: '',
          publisher: '',
          authors: '',
          isbn: '',
          edition: '',
          year: undefined,
          coverImageUrl: '',
          description: '',
        });
      }} maxWidth="md" fullWidth>
        <DialogTitle>{selectedBook ? 'Edit Book' : 'Add Book'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Book Title"
              fullWidth
              required
              value={bookFormData.title}
              onChange={(e) => setBookFormData({ ...bookFormData, title: e.target.value })}
            />
            <TextField
              label="Publisher"
              fullWidth
              required
              value={bookFormData.publisher}
              onChange={(e) => setBookFormData({ ...bookFormData, publisher: e.target.value })}
            />
            <TextField
              label="Authors (comma-separated)"
              fullWidth
              value={bookFormData.authors || ''}
              onChange={(e) => setBookFormData({ ...bookFormData, authors: e.target.value })}
            />
            <TextField
              label="ISBN"
              fullWidth
              value={bookFormData.isbn || ''}
              onChange={(e) => setBookFormData({ ...bookFormData, isbn: e.target.value })}
            />
            <TextField
              label="Edition"
              fullWidth
              value={bookFormData.edition || ''}
              onChange={(e) => setBookFormData({ ...bookFormData, edition: e.target.value })}
            />
            <TextField
              label="Year"
              type="number"
              fullWidth
              value={bookFormData.year || ''}
              onChange={(e) => setBookFormData({ ...bookFormData, year: e.target.value ? parseInt(e.target.value) : undefined })}
            />
            <TextField
              label="Cover Image URL"
              fullWidth
              value={bookFormData.coverImageUrl || ''}
              onChange={(e) => setBookFormData({ ...bookFormData, coverImageUrl: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={bookFormData.description || ''}
              onChange={(e) => setBookFormData({ ...bookFormData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setBookDialogOpen(false);
            setSelectedBook(null);
            setBookFormData({
              title: '',
              publisher: '',
              authors: '',
              isbn: '',
              edition: '',
              year: undefined,
              coverImageUrl: '',
              description: '',
            });
          }}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            try {
              if (!bookFormData.title || !bookFormData.publisher) {
                setError('Title and Publisher are required');
                return;
              }

              let createdBookId: number | null = null;
              if (selectedBook?.id) {
                await updateBook(selectedBook.id, bookFormData);
                createdBookId = selectedBook.id;
              } else {
                const createdBook = await createBook(bookFormData);
                createdBookId = createdBook.id || null;
              }

              await loadBooks();

              setBookDialogOpen(false);
              setSelectedBook(null);
              setBookFormData({
                title: '',
                publisher: '',
                authors: '',
                isbn: '',
                edition: '',
                year: undefined,
                coverImageUrl: '',
                description: '',
              });

              // If we came from view dialog (indicated by viewDialogOpen being false but we were in BOOK_BASED mode)
              // Check if we should reopen the view dialog
              const shouldReopenViewDialog = viewType === 'BOOK_BASED';
              if (shouldReopenViewDialog && createdBookId) {
                setSelectedBookId(createdBookId);
                // Small delay to ensure state is updated
                setTimeout(() => {
                  setViewDialogOpen(true);
                }, 100);
              }
            } catch (err: any) {
              console.error('Failed to save book:', err);
              setError(err.response?.data?.message || 'Failed to save book');
            }
          }}>
            {selectedBook ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resource Dialog */}
      <Dialog open={resourceDialogOpen} onClose={() => setResourceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedResource ? 'Edit Resource' : 'Add Resource'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Resource Type</InputLabel>
              <Select label="Resource Type" defaultValue={selectedResource?.resourceType || 'FILE'}>
                <MenuItem value="FILE">Upload File</MenuItem>
                <MenuItem value="URL">External Link</MenuItem>
                <MenuItem value="VIDEO">Video</MenuItem>
                <MenuItem value="AUDIO">Audio</MenuItem>
                <MenuItem value="IMAGE">Image</MenuItem>
                <MenuItem value="INTERACTIVE">Interactive Content</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Resource Name" fullWidth defaultValue={selectedResource?.name || ''} />
            <TextField label="Description" fullWidth multiline rows={2} defaultValue={selectedResource?.description || ''} />
            {selectedResource?.resourceType === 'URL' || !selectedResource ? (
              <TextField label="External URL" fullWidth defaultValue={selectedResource?.externalUrl || ''} />
            ) : (
              <Button variant="outlined" startIcon={<Upload />} fullWidth>
                Upload File
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResourceDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setResourceDialogOpen(false)}>
            {selectedResource ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Content Editor Dialog */}
      <Dialog open={contentEditorOpen} onClose={() => setContentEditorOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Edit Topic Content - {selectedTopic?.title}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, minHeight: 400 }}>
            <TextField
              label="Content"
              fullWidth
              multiline
              rows={15}
              defaultValue={selectedTopic?.content || ''}
              placeholder="Enter rich text content here. Support for formatting, links, images, and embedded resources will be added."
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Linked Resources
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><ResourceIcon /></ListItemIcon>
                  <ListItemText primary="Sample Resource" secondary="PDF • 1.2 MB" />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
              <Button startIcon={<AddIcon />} size="small" sx={{ mt: 1 }}>
                Link Resource
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContentEditorOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setContentEditorOpen(false)}>
            Save Content
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add-child dialog for structure tree */}
      <Dialog open={addChildDialogOpen} onClose={() => setAddChildDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {addChildMode === 'wrapper' ? 'Add child wrapper' : 'Add custom topic'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={addChildMode === 'wrapper' ? 'Wrapper name' : 'Topic name'}
            fullWidth
            value={addChildName}
            onChange={(e) => setAddChildName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirmAddChild();
              }
            }}
          />
          {addChildMode === 'topic' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Creates a course-specific topic (no core-subject link required).
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddChildDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmAddChild} disabled={!addChildName.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        {menuItem?.type === 'ADD_CHILD' && menuItem?.node && [
          <MenuItem key="add-child-wrapper" onClick={() => {
            openAddChildDialog(menuItem.node, 'wrapper');
            handleMenuClose();
          }}>
            <FolderIcon sx={{ mr: 1 }} fontSize="small" />
            Add wrapper
          </MenuItem>,
          <MenuItem key="add-child-topic" onClick={() => {
            openAddChildDialog(menuItem.node, 'topic');
            handleMenuClose();
          }}>
            <FileIcon sx={{ mr: 1 }} fontSize="small" />
            Add custom topic
          </MenuItem>,
        ]}
        {tabValue === 1 && menuItem && !menuItem?.type && [
          <MenuItem key="customize" onClick={() => {
            setSelectedTopic(menuItem);
            setTopicDialogOpen(true);
            handleMenuClose();
          }}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Customize (Rename/Reorder)
          </MenuItem>,
          <MenuItem key="edit-content" onClick={() => {
            setSelectedTopic(menuItem);
            setContentEditorOpen(true);
            handleMenuClose();
          }}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit Content
          </MenuItem>,
          <MenuItem key="remove" onClick={async () => {
            if (!courseId || !menuItem?.id) return;
            try {
              await adminApi.delete(`/api/admin/courses/${courseId}/topics/${menuItem.id}`);
              handleMenuClose();
              // Reload course topics after deletion
              await loadCourseTopics();
            } catch (err: any) {
              setError(err.response?.data?.message || 'Failed to remove topic from course');
              handleMenuClose();
            }
          }} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Remove from Course
          </MenuItem>
        ]}
        {tabValue === 0 && menuItem && [
          <MenuItem key="edit-view" onClick={() => {
            setSelectedView(menuItem);
            setViewType(menuItem.viewType || 'DEFAULT');
            setSelectedBookId(menuItem.bookId || null);
            setViewFormData({ name: menuItem.name || '', isDefault: menuItem.isDefault || false });
            setViewDialogOpen(true);
            handleMenuClose();
          }}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>,
          <MenuItem key="topic-management" onClick={() => {
            // Switch to Topics tab for this view
            setTabValue(1);
            handleMenuClose();
            // TODO: Filter topics by view
          }}>
            <FolderIcon sx={{ mr: 1 }} fontSize="small" />
            Topic Management
          </MenuItem>,
          <MenuItem key="delete-view" onClick={async () => {
            if (!courseId || !menuItem?.id) return;
            try {
              await deleteView(parseInt(courseId), menuItem.id);
              await loadViews();
              handleMenuClose();
            } catch (err: any) {
              setError(err.response?.data?.message || 'Failed to delete view');
              handleMenuClose();
            }
          }} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        ]}
        {tabValue === 2 && menuItem && [
          <MenuItem key="edit-book" onClick={() => {
            setSelectedBook(menuItem);
            setBookFormData({
              title: menuItem.title || '',
              publisher: menuItem.publisher || '',
              authors: menuItem.authors || '',
              isbn: menuItem.isbn || '',
              edition: menuItem.edition || '',
              year: menuItem.year,
              coverImageUrl: menuItem.coverImageUrl || '',
              description: menuItem.description || '',
            });
            setBookDialogOpen(true);
            handleMenuClose();
          }}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>,
          <MenuItem key="delete-book" onClick={async () => {
            if (!menuItem?.id) return;
            try {
              await deleteBook(menuItem.id);
              await loadBooks();
              handleMenuClose();
            } catch (err: any) {
              setError(err.response?.data?.message || 'Failed to delete book');
              handleMenuClose();
            }
          }} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        ]}
        {tabValue === 3 && [
          <MenuItem key="edit-resource" onClick={() => {
            setSelectedResource(menuItem);
            setResourceDialogOpen(true);
            handleMenuClose();
          }}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>,
          <MenuItem key="delete-resource" onClick={handleMenuClose} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        ]}
      </Menu>
    </Container>
  );
};

export default ContentManagement;

